import React, { ReactElement, useCallback } from 'react';

import useNewSkin from '../../hooks/useNewSkin';
import usePriceFormatter from './usePriceFormatter';

import { getFullParsedUrl, openLink } from '../../helpers';
import { ravenCaptureException } from '../../helpers/raven';
import { shopifyAddToCartRequest } from '../../helpers/shopifyApiData';

import { getTextStyles } from '../../styles/text.styles';
import Translation from '../Translation';
import { Arrow } from '../svgIcons/SvgIcons';
import AddToCartButton from './AddToCartButton';
import styles from './ProductCard.styles';

export type AiAssistantProductCardProps = {
    id: number;
    title: string;
    price: number;
    basePrice?: number | null;
    currency: string;
    url: string;
    imageUrl: string | null;
    isAddToCartEnabled: boolean;
    messageId: string;
};

const AiAssistantProductCard = ({
    id,
    title,
    price,
    basePrice,
    currency,
    url,
    imageUrl,
    isAddToCartEnabled,
    messageId,
}: AiAssistantProductCardProps): ReactElement => {
    const { isNewSkin } = useNewSkin();
    const textStyles = getTextStyles(isNewSkin);
    const parsedUrl = getFullParsedUrl(url);

    const shouldShowBasePrice = basePrice && price !== basePrice;

    const formatter = usePriceFormatter(currency);

    const handleAddToCart = useCallback(async (): Promise<boolean> => {
        try {
            const response = await shopifyAddToCartRequest(id);
            if (!response.ok) {
                ravenCaptureException('Shopify add to cart request failed', {
                    message: response.statusText,
                });
                return false;
            }
            return true;
        } catch (error) {
            if (error instanceof Error) {
                ravenCaptureException('Shopify add to cart request failed', {
                    message: error.message,
                });
            }
            return false;
        }
    }, [id]);

    const handleViewDetails = useCallback((): void => {
        openLink(parsedUrl);
    }, [parsedUrl]);

    return (
        <div css={styles.container}>
            {isAddToCartEnabled && (
                <button type="button" css={styles.clickableContainer} onClick={handleViewDetails}>
                    <Translation value="recommendProductViewDetails" fallback="View details" />
                </button>
            )}

            {imageUrl && <img src={imageUrl} alt={title} css={styles.image} />}

            <div css={styles.contentContainer}>
                <div css={styles.textAndPricesContainer}>
                    <p css={[styles.titleText, textStyles.text14Medium]}>{title}</p>
                    {formatter && (
                        <div css={styles.pricesContainer}>
                            {shouldShowBasePrice && (
                                <p css={[styles.basePriceText, textStyles.text14Medium]}>
                                    {formatter.format(basePrice)}
                                </p>
                            )}
                            <p css={[styles.titleText, textStyles.text14Medium]}>
                                {formatter.format(price)}
                            </p>
                        </div>
                    )}
                </div>

                <div css={styles.buttonsContainer}>
                    {isAddToCartEnabled ? (
                        <AddToCartButton
                            productVariantId={id}
                            messageId={messageId}
                            onAddToCart={handleAddToCart}
                        />
                    ) : (
                        <button
                            type="button"
                            css={styles.viewDetailsButton(isNewSkin)}
                            onClick={handleViewDetails}
                        >
                            <Translation
                                value="recommendProductViewDetails"
                                fallback="View details"
                            />
                            <Arrow />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AiAssistantProductCard;
